/*
  Pocket: the gift card "Email it to: Me / Them" choice
  (snippets/gift-card-recipient-form.liquid).

  "Them" shows and turns on the recipient fields; "Me" hides and turns them off
  again, so nothing extra is sent and Shopify emails the card to the buyer as
  usual. Before Add to Cart it checks the email and the send date, because the
  product form switches off the browser's own checks (novalidate), and it adds
  the shopper's timezone so a scheduled card arrives on the right day, around
  9am.

  It also stops the delivery estimate app adding an "Arrives between" date to the
  gift card: an emailed card has nothing to deliver.
*/
if (!customElements.get('recipient-form')) {
  const MAX_DAYS_AHEAD = 90; // Shopify's limit for scheduled gift cards
  const SEND_LATER_MINUTES = 180; // moves a scheduled card from about 6am to about 9am

  const isoDate = (date) =>
    [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-');

  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      connectedCallback() {
        this.form = this.closest('form');
        this.them = this.querySelector('.recipient-form__choice input[value="them"]');
        this.meNote = this.querySelector('[data-note="me"]');
        this.fields = this.querySelector('.recipient-form__fields');
        this.email = this.querySelector('[name="properties[Recipient email]"]');
        this.message = this.querySelector('[name="properties[Message]"]');
        this.sendOn = this.querySelector('[name="properties[Send on]"]');
        this.offset = this.querySelector('[name="properties[__shopify_offset]"]');
        this.count = this.querySelector('[data-count]');
        this.error = this.querySelector('.recipient-form__error');
        if (!this.form || !this.them || !this.fields) return;

        // Worked out in the shopper's own time, not the server's.
        const today = new Date();
        this.sendOn.min = isoDate(today);
        this.sendOn.max = isoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + MAX_DAYS_AHEAD));

        this.querySelectorAll('.recipient-form__choice input').forEach((radio) => {
          radio.addEventListener('change', () => this.toggle());
        });
        this.message.addEventListener('input', () => {
          this.count.textContent = this.message.value.length;
        });
        this.fields.addEventListener('input', () => this.showError(''));
        this.toggle();

        // Capture phase on the document runs before the theme's product form
        // handler, so a form that would only fail at Shopify never gets sent.
        // The sticky Add to Cart submits this same form.
        this.onSubmit = (event) => this.check(event);
        document.addEventListener('submit', this.onSubmit, true);
      }

      disconnectedCallback() {
        if (this.onSubmit) document.removeEventListener('submit', this.onSubmit, true);
      }

      toggle() {
        const on = this.them.checked;
        this.fields.hidden = !on;
        if (this.meNote) this.meNote.hidden = on;
        this.fields.querySelectorAll('input, textarea').forEach((input) => {
          input.disabled = !on;
        });
        if (!on) this.showError('');
      }

      check(event) {
        if (event.target !== this.form) return;

        this.form.querySelectorAll('[name="properties[Arrives between]"]').forEach((input) => {
          input.disabled = true;
        });
        document.querySelectorAll(`[form="${this.form.id}"][name="properties[Arrives between]"]`).forEach((input) => {
          input.disabled = true;
        });

        if (!this.them.checked) return;

        this.email.value = this.email.value.trim();
        let problem = '';
        let field = null;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email.value)) {
          problem = this.email.value ? "That email address doesn't look right." : 'Add their email so we know where to send it.';
          field = this.email;
        } else if (this.sendOn.value && (this.sendOn.value < this.sendOn.min || this.sendOn.value > this.sendOn.max)) {
          problem = `Pick a send date between today and ${MAX_DAYS_AHEAD} days from now.`;
          field = this.sendOn;
        }

        if (problem) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(problem);
          // The sticky Add to Cart can be far below the form, so bring the
          // field into view before focusing it.
          field.scrollIntoView({ block: 'center', behavior: 'smooth' });
          field.focus({ preventScroll: true });
          return;
        }

        // Shopify sends scheduled cards in an early morning run for the timezone
        // it's given, which landed around 6am. Telling it the shopper is 3 hours
        // further west moves that to around 9am their time, on the same date.
        // 720 (UTC-12) is the largest offset a timezone has.
        this.offset.value = Math.min(720, new Date().getTimezoneOffset() + SEND_LATER_MINUTES);
      }

      showError(text) {
        this.error.textContent = text;
        this.error.hidden = !text;
      }
    }
  );
}
